import { BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM, BRANCH_ENVIRONMENT_PRODUCTION_ENUM } from '@takeshape/branches';
import type { SagaIterator } from 'redux-saga';
import { put, race, take, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { compatSchemaToSchema } from '../../compat/utils.js';
import { BranchMergeOperation, openBranchMerge, setBranchMergeCanMerge, setBranchMergeError } from '../actions.js';
import type { DevelopmentBranch, MergedBranchResponse, ProductionBranch } from '../api.js';
import { fetchSchemaRoutine } from './fetch-schema.js';
import { mergeBranchRoutine } from './merge-branch.js';

export function* handleOpenBranchMerge({ payload }: ActionType<typeof openBranchMerge>): SagaIterator {
  const { operation, branchName } = payload;

  yield put(
    fetchSchemaRoutine.requested(undefined, {
      branch: 'production',
      runtime: false
    })
  );

  const { rejected: fetchRejected } = yield race({
    fulfilled: take(fetchSchemaRoutine.fulfilled),
    rejected: take(fetchSchemaRoutine.rejected)
  });

  if (fetchRejected) {
    yield put(setBranchMergeError('Error fetching production'));
    return;
  }

  const head: DevelopmentBranch = {
    environment: BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM,
    branchName
  };

  const base: ProductionBranch = {
    environment: BRANCH_ENVIRONMENT_PRODUCTION_ENUM
  };

  yield put(
    mergeBranchRoutine.requested(undefined, {
      head,
      base,
      target: {
        ...(operation === BranchMergeOperation.Promote ? base : head),
        version: 1
      },
      dryRun: true
    })
  );

  const { fulfilled: dryMergeFulfilled, rejected: dryMergeRejected } = yield race({
    fulfilled: take(mergeBranchRoutine.fulfilled),
    rejected: take(mergeBranchRoutine.rejected)
  });

  if (dryMergeFulfilled) {
    const { mergedBranch } = dryMergeFulfilled.payload as MergedBranchResponse;
    const mergedSchema = mergedBranch.latestVersion.schema;

    yield put(setBranchMergeCanMerge(compatSchemaToSchema(mergedSchema)));
  } else {
    yield put(setBranchMergeError(dryMergeRejected.payload));
  }
}

export default function* root(): SagaIterator {
  yield takeLatest(openBranchMerge, handleOpenBranchMerge);
}
